<template>
  <Flex
    direction="column"
    :class="$style.base"
  >
    <Variables
      v-if="showVariablesModal"
      @close="showVariablesModal = false"
    />
    <MessageField
      v-show="showError"
      danger
      v-test="'bannerError'"
    >
      <BaseText>{{ $t('global.image_upload_fail') }}</BaseText>
      <BaseText small>{{ $t('global.image_format_not_supported') }}</BaseText>
    </MessageField>
    <BaseInput
      v-model="subject"
      :label="$t('global.title')"
      required
      v-test="'messageTitle'"
    />
    <BaseEditor
      v-model="message"
      :cloudinaryName="UploadedImageUsage.NewsletterContent"
      required
      :autocompleteOptions="{
        name: 'email_variables_autocomplete',
        ch: AUTOCOMPLETE_CHARACTERS,
        fetch: handleAutocompleteFetch,
        onAction: handleAutocompleteAction
      }"
      @error="onError"
      v-test="'messageContent'"
    />
    <BaseText
      @click="showVariablesModal = true"
      v-test="'openVariablesModal'"
    >
      {{ $t('marketing.email.sections.personalize_email') }}
    </BaseText>
  </Flex>
</template>

<script lang="ts">
import useVuelidate from '@vuelidate/core';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import Variables from '@/modules/marketing/modals/Variables.vue';
import { UploadedImageUsage } from '@/types';
import { useMarketingStore } from '../../store';
import { useMarketingBuilderStore } from '../../store/builder';
import MessageField from './MessageField.vue';

export default {
  components: {
    Variables,
    MessageField,
  },
  setup() {
    const builderStore = useMarketingBuilderStore();
    const { emailType, allowImageUpload } = storeToRefs(builderStore);
    const { subject, message, isSubmitted, messageIsInvalid } = storeToRefs(
      useMarketingStore(),
    );
    return {
      v$: useVuelidate(),
      UploadedImageUsage,
      emailType,
      allowImageUpload,
      subject,
      message,
      isSubmitted,
      messageIsInvalid,
    };
  },
  data() {
    return {
      showVariablesModal: false,
      showError: false,
      preset: '',
      AUTOCOMPLETE_CHARACTERS: '{{',
      templates: [],
    };
  },
  watch: {
    'v$.$invalid': {
      handler() {
        this.messageIsInvalid = this.v$.$invalid;
      },
      immediate: true,
    },
    isSubmitted() {
      this.v$.$touch();
    },
  },
  computed: {
    template() {
      return this.templates.find((template) => {
        if (this.emailType === 'REBOOK_REMINDER') {
          return template.id === 'rebook_reminder_email';
        } else if (this.emailType === 'BIRTHDAY') {
          return template.id === 'marketing_birthday_message';
        } else if (this.emailType === 'ONLINE_FORMS') {
          return template.id === 'online_forms_email';
        } else {
          return template.id === 'marketing_email';
        }
      });
    },
  },
  methods: {
    onError() {
      this.showError = true;
      setTimeout(() => {
        this.showError = false;
      }, 3000);
    },
    getMatchedChars(pattern: string) {
      if (this.template?.variables) {
        return this.template?.variables.filter(
          (char) => char.indexOf(pattern) !== -1,
        );
      }
      return [];
    },
    handleAutocompleteFetch(pattern: string) {
      return new Promise((resolve) => {
        if (this.template?.variables) {
          return resolve(
            this.getMatchedChars(pattern).map((variable) => ({
              type: 'autocompleteitem',
              value: variable,
              text: variable,
            })),
          );
        }
        resolve([]);
      });
    },
    handleAutocompleteAction(
      editor: any,
      autocompleteApi: any,
      rng: any,
      value: any,
    ) {
      editor.selection.setRng(rng);
      editor.insertContent(`{{${value}}}`);
      autocompleteApi.hide();
    },
  },
  apollo: {
    templates: {
      query: gql`
        query templates {
          templates {
            id
            variables
          }
        }
      `,
    },
  },
};
</script>

<style
  lang="scss"
  module
>
.base {
  border-top: 1px solid var(--color-border);
  padding: $spacing 0;
}
</style>
